import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import usePageTitle from "../helpers/usePageTitle";
import Toggle from "../components/shared/Toggle";
import SequenceCanvas from "../components/campaignWizard/SequenceCanvas";
import {
  getCampaignByIdPromise,
  getCampaignPerformancePromise,
  getCampaignSequencePerformancePromise,
  getCampaignSenderPerformancePromise,
  getCampaignLeadsPromise,
  getCampaignAnalytics,
  updateCampaignPromise,
  pauseCampaignPromise,
  resumeCampaignPromise,
  startCampaignPromise,
  exportCampaignLeadsPromise,
  removePendingCampaignLeadsPromise,
  getLinkedInAccounts,
} from "../services/apiService";
import { accountNeedsAttention } from "../components/linkedinAccounts/accountState";
import { notifyError } from "../helpers/apiError";

const TERMINAL_STATUSES = new Set(["CANCELLED"]);
const TOGGLEABLE_ON = new Set(["DRAFT", "PAUSED", "COMPLETED"]);

const TABS = [
  { id: "campaign", label: "Campaign Performance" },
  { id: "sequence", label: "Sequence Performance" },
  { id: "sender", label: "Sender Performance" },
  { id: "leads", label: "Lead Analytics" },
];

function StatCard({ label, value, delta }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value ?? 0}</div>
      <div className="text-xs text-gray-400 mt-1">+{delta ?? "0.0"}%</div>
    </div>
  );
}

export default function CampaignView() {
  const { lng, id } = useParams();
  const navigate = useNavigate();
  usePageTitle("View Campaign");

  const [tab, setTab] = useState("campaign");
  const [campaign, setCampaign] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [seqPerf, setSeqPerf] = useState(null);
  const [senderPerf, setSenderPerf] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsMeta, setLeadsMeta] = useState({ page: 1, total: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        getCampaignByIdPromise(id),
        getCampaignPerformancePromise(id),
      ]);
      if (!cRes.data?.id) {
        setCampaign(null);
        setNotFound(true);
        return;
      }
      setCampaign(cRes.data);
      setNameDraft(cRes.data.name);
      setPerformance(pRes.data);
      setNotFound(false);
    } catch (err) {
      const code = err?.status ?? err?.response?.status;
      if (code === 404 || code === 400) setNotFound(true);
      else toast.error("Failed to load campaign");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    getLinkedInAccounts()
      .then((res) => {
        const body = res.data;
        setAccounts(Array.isArray(body) ? body : body?.data || []);
      })
      .catch(() => setAccounts([]));
  }, []);

  useEffect(() => {
    if (tab === "sequence") {
      getCampaignSequencePerformancePromise(id)
        .then((res) => setSeqPerf(res.data))
        .catch(() => setSeqPerf(null));
    }
    if (tab === "sender") {
      getCampaignSenderPerformancePromise(id)
        .then((res) => setSenderPerf(res.data?.senders || res.data || []))
        .catch(() => setSenderPerf([]));
    }
    if (tab === "leads") {
      getCampaignLeadsPromise(id, { page: 1, limit: 20, search: search || undefined })
        .then((res) => {
          setLeads(res.data?.data || res.data?.leads || []);
          setLeadsMeta({
            page: res.data?.page || 1,
            total: res.data?.total || 0,
          });
        })
        .catch(() => setLeads([]));
    }
    if (tab === "campaign") {
      getCampaignAnalytics(id)
        .then((res) => setAnalytics(res.data))
        .catch(() => setAnalytics(null));
    }
  }, [tab, id, search]);

  const status = campaign?.status;
  const isActive = status === "ACTIVE";

  const campaignSenders = useMemo(() => {
    const ids = campaign?.senders?.length
      ? campaign.senders.map((sender) => sender.id)
      : [campaign?.linkedinAccountId].filter(Boolean);
    return ids.map((senderId) => accounts.find((a) => a.id === senderId)).filter(Boolean);
  }, [campaign, accounts]);
  const allSendersBlocked = isActive && campaignSenders.length > 0 && campaignSenders.every(accountNeedsAttention);
  const fallbackSenderName = campaign?.senders?.length === 1 ? campaign.senders[0].accountName : null;
  const isToggleDisabled = TERMINAL_STATUSES.has(status);


  const toggleActive = async (nextChecked) => {
    if (isToggleDisabled) {
      toast.error("Cancelled campaigns cannot be restarted");
      return;
    }
    try {
      if (isActive && !nextChecked) {
        await pauseCampaignPromise(id);
        toast.success("Campaign paused");
      } else if (status === "PAUSED" && nextChecked) {
        await resumeCampaignPromise(id);
        toast.success("Campaign resumed");
      } else if ((status === "DRAFT" || status === "COMPLETED") && nextChecked) {
        await startCampaignPromise(id);
        toast.success(status === "COMPLETED" ? "Campaign restarted" : "Campaign started");
      } else if (!TOGGLEABLE_ON.has(status) && nextChecked) {
        toast.error(`Cannot activate a ${status?.toLowerCase()} campaign`);
        return;
      } else {
        return;
      }
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  const saveName = async () => {
    try {
      await updateCampaignPromise(id, { name: nameDraft });
      setCampaign((prev) => ({ ...prev, name: nameDraft }));
      setRenaming(false);
    } catch {
      toast.error("Failed to rename");
    }
  };

  const chartData = useMemo(() => {
    if (analytics?.timeSeries) return analytics.timeSeries;
    if (analytics?.daily) return analytics.daily;
    return [];
  }, [analytics]);

  const senderChartData = useMemo(() => {
    return (Array.isArray(senderPerf) ? senderPerf : []).map((row) => ({
      name: row.accountName || row.name || "Sender",
      connectionsSent: row.connectionsSent ?? row.connections ?? 0,
      connectionsAccepted: row.connectionsAccepted ?? 0,
      messages: row.messagesSent ?? row.messages ?? 0,
      replies: row.replies ?? 0,
    }));
  }, [senderPerf]);

  if (loading && !campaign) {
    return <div className="p-8 text-gray-400">Loading…</div>;
  }

  if (!campaign) {
    return (
      <div className="p-8 text-center space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">{notFound ? "Campaign not found" : "Couldn't load campaign"}</h1>
        <p className="text-sm text-gray-500">
          {notFound ? "It may have been deleted, or the link is wrong." : "Please try again in a moment."}
        </p>
        <button type="button" onClick={() => navigate(`/${lng}/campaigns`)} className="text-sm text-blue-600">
          ← Back to campaigns
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(`/${lng}/campaigns`)}
            className="text-sm text-gray-500"
          >
            ← View Campaign
          </button>
          <Toggle
            checked={isActive}
            onChange={toggleActive}
            disabled={isToggleDisabled}
          />
          {renaming ? (
            <div className="flex gap-2">
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                className="border rounded-lg px-2 py-1 text-sm"
              />
              <button type="button" onClick={saveName} className="text-sm text-blue-600">
                Save
              </button>
            </div>
          ) : (
            <h1 className="text-xl font-semibold truncate flex items-center gap-2">
              {campaign?.name}
              <button type="button" onClick={() => setRenaming(true)} className="text-gray-400 text-sm">
                ✎
              </button>
            </h1>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/${lng}/campaigns/${id}/edit`)}
          className="px-3 py-1.5 rounded-lg border text-sm"
        >
          Edit
        </button>
      </div>

      {allSendersBlocked ? (
        <div role="alert" className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          This campaign is active, but all of its senders need attention, so nothing is being sent.
          {" "}
          {campaignSenders.map((a) => `${a.accountName}${a.connectionError ? ` (${a.connectionError})` : ""}`).join(", ")}
          {" "}
          — fix them on the{" "}
          <button type="button" onClick={() => navigate(`/${lng}/linkedin-accounts`)} className="underline font-medium">
            LinkedIn accounts
          </button>{" "}
          page.
        </div>
      ) : null}

      <div className="flex gap-2 flex-wrap border-b border-gray-100 pb-2">
        {TABS.map((t, index) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                active ? "bg-gray-100 font-medium" : "text-gray-500"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center border ${
                  active ? "bg-blue-500 border-blue-500 text-white" : "border-blue-400 text-blue-500"
                }`}
              >
                {active ? "●" : "✓"}
              </span>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "campaign" ? (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
            <div className="text-sm">
              Status:{" "}
              <span className="font-medium text-amber-600">{campaign?.status}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500"
                style={{ width: `${performance?.progressPercent || 0}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              <div>Total leads: {performance?.totalLeads ?? performance?.leadBuckets?.total ?? 0}</div>
              <div>In progress: {performance?.leadBuckets?.inProgress ?? 0}</div>
              <div>Not started yet: {performance?.leadBuckets?.notStarted ?? 0}</div>
              <div>Finished: {performance?.leadBuckets?.finished ?? 0}</div>
              <div>Paused: {performance?.leadBuckets?.paused ?? 0}</div>
              <div>Failed: {performance?.leadBuckets?.failed ?? 0}</div>
              <div>Excluded: {performance?.leadBuckets?.excluded ?? 0}</div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                label="Connections Accepted"
                value={analytics?.connectionsAccepted ?? analytics?.kpis?.connectionsAccepted}
              />
              <StatCard
                label="Message Replies"
                value={analytics?.messageReplies ?? analytics?.kpis?.messageReplies}
              />
              <StatCard
                label="InMail Replies"
                value={analytics?.inMailReplies ?? analytics?.kpis?.inMailReplies}
              />
              <StatCard
                label="Interested Leads"
                value={analytics?.interestedLeads ?? analytics?.kpis?.interestedLeads}
              />
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-4 h-72">
              {chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-300 text-sm">
                  No chart data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#93c5fd" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {tab === "sequence" ? (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden h-[70vh]">
          <div className="flex justify-end px-4 py-2 border-b">
            <button type="button" className="text-sm border rounded-lg px-3 py-1.5">
              Export sequence
            </button>
          </div>
          <SequenceCanvas
            steps={seqPerf?.steps || []}
            stepStats={seqPerf?.stepStats || []}
            readOnly
            autoLayout
          />
        </div>
      ) : null}

      {tab === "sender" ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4">
          <div className="h-80">
            {senderChartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-300">
                No sender data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={senderChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="connectionsSent" fill="#3b82f6" name="Connections Sent" />
                  <Bar dataKey="connectionsAccepted" fill="#22c55e" name="Connections Accepted" />
                  <Bar dataKey="messages" fill="#a855f7" name="Messages" />
                  <Bar dataKey="replies" fill="#ef4444" name="Replies" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      ) : null}

      {tab === "leads" ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Leads"
              className="border rounded-xl px-3 py-2 text-sm flex-1 min-w-[180px]"
            />
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await exportCampaignLeadsPromise(id);
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `campaign-${id}-leads.csv`;
                  a.click();
                } catch {
                  toast.error("Export failed");
                }
              }}
              className="px-3 py-2 rounded-xl border text-sm"
            >
              Export as CSV
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await removePendingCampaignLeadsPromise(id);
                  toast.success("Pending leads removed");
                  setSearch((s) => s);
                } catch {
                  toast.error("Failed to remove pending leads");
                }
              }}
              className="px-3 py-2 rounded-xl border text-sm text-red-600"
            >
              Remove pending leads
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-2">Lead</th>
                  <th className="py-2 pr-2">Progress</th>
                  <th className="py-2 pr-2">Last Action</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2">Tags</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-400">
                      No leads
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-gray-50">
                      <td className="py-3 pr-2">
                        <div className="font-medium">
                          {lead.firstName} {lead.lastName}
                        </div>
                        <div className="text-xs text-gray-400">
                          Sender: {lead.senderName || fallbackSenderName || "—"}
                        </div>
                      </td>
                      <td className="py-3 pr-2">
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs">
                          {lead.progressLabel || lead.progress || "Not started"}
                        </span>
                      </td>
                      <td className="py-3 pr-2 text-gray-500">
                        {lead.lastActionAt ? new Date(lead.lastActionAt).toLocaleString() : lead.lastAction || "—"}
                      </td>
                      <td className="py-3 pr-2 text-xs text-gray-500">
                        {lead.executionStatus || lead.status || "—"}
                      </td>
                      <td className="py-3">
                        {(lead.tags || []).slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="inline-block mr-1 px-2 py-0.5 rounded-full bg-gray-100 text-[10px]"
                          >
                            {tag}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-gray-400">
            Showing {leads.length} of {leadsMeta.total}
          </div>
        </div>
      ) : null}
    </div>
  );
}
